/**
 * SubscriptionPlan — the single shared pricing component.
 *
 * Two modes, one component:
 *
 *   mode="display" (default — landing page):
 *     Each plan is a CTA. Clicking writes the chosen plan to the
 *     onboarding store as `selectedPlanIntent`, then navigates to
 *     /register?plan=...&billing=... so the choice is also in the URL
 *     (refresh-safe, shareable, survives a tab restart). Enterprise →
 *     /book-demo.
 *
 *   mode="select" (register page):
 *     Plans are SELECTABLE cards. Active selection is highlighted; the
 *     parent owns selection state via `selectedPlanId` + `onPlanSelect`
 *     and `billing` + `onBillingChange`. The Enterprise card is shown
 *     for completeness but not selectable — it offers a "Talk to sales"
 *     link to /book-demo instead. (Enterprise can't self-signup.)
 *
 * Pricing source:
 *   1. Loads /subscription-plans/public (live prices + limits from the
 *      database) and overlays them on the launch plans.
 *   2. Falls back to the launch plans in constants/plans.ts on failure.
 *
 * Launch plans (Oct 2026): Start 149, Growth 299, Business 599 per month;
 * annual = 10 × monthly. Enterprise has no public price — "Talk to sales".
 */
import { usePublicPlans } from "@/hooks/usePublicPlans";
import PlanPrice from "@/components/common/PlanPrice";
import { PricesFor } from "@/components/common/PricesFor";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Sparkles, Building2 } from "lucide-react";
import { Button } from "../ui/button";
import { useOnboardingStore } from "@/stores/onboardingStore";

// ─── Types & plans (single source: constants/plans.ts) ─────────────

import {
  LAUNCH_PLANS,
  planById as findPlan,
  planByBackendName,
  type BillingCycle,
  type PlanId,
  type LaunchPlan,
} from "@/constants/plans";

export type { BillingCycle, PlanId };
type PublicPlan = LaunchPlan;

interface SubscriptionPlanProps {
  /** "display" (landing CTA) or "select" (register selection). Default: "display" */
  mode?: "display" | "select";
  /** Selectable mode only: currently-selected plan id */
  selectedPlanId?: PlanId;
  /** Selectable mode only: called when user picks a plan */
  onPlanSelect?: (planId: PlanId) => void;
  /** Optional: controlled billing cycle (otherwise internal state) */
  billing?: BillingCycle;
  /** Optional: called when user toggles billing */
  onBillingChange?: (billing: BillingCycle) => void;
}

/** Kept for existing imports (Register.tsx). */
export const STATIC_PLANS: PublicPlan[] = LAUNCH_PLANS;

// ─── Helpers exported for Register.tsx so it has the same lookup ───

export function planById(id: string): PublicPlan | undefined {
  return findPlan(id);
}

export function planByBackendEnum(name: string): PublicPlan | undefined {
  return planByBackendName(name);
}


// ─── Component ─────────────────────────────────────────────────────

const SubscriptionPlan: React.FC<SubscriptionPlanProps> = ({
  mode = "display",
  selectedPlanId,
  onPlanSelect,
  billing: controlledBilling,
  onBillingChange,
}) => {
  const { plans } = usePublicPlans(STATIC_PLANS);
  const [internalBilling, setInternalBilling] =
    useState<BillingCycle>("monthly");
  const navigate = useNavigate();
  const setSelectedPlanIntent = useOnboardingStore(
    (s) => s.setSelectedPlanIntent,
  );

  // Billing can be either controlled (Register passes it in) or
  // uncontrolled (landing manages its own toggle).
  const billing = controlledBilling ?? internalBilling;
  const handleBillingChange = (next: BillingCycle) => {
    if (onBillingChange) onBillingChange(next);
    else setInternalBilling(next);
  };


  // Annual prices are 10 × monthly: two months free.
  const yearlySavingLabel = useMemo(() => "2 months free", []);

  /**
   * Landing-mode click handler: writes intent to store + navigates with
   * the choice in the query string. Both layers per the user's choice
   * ("for safety, so when there is break on falls on the other").
   */
  const handleDisplayClick = (plan: PublicPlan) => {
    if (plan.isEnterprise) {
      navigate("/enterprise");
      return;
    }
    setSelectedPlanIntent({
      planId: plan.id,
      backendEnum: plan.backendEnum,
      billingCycle: billing,
    });
    navigate(`/register?plan=${plan.id}&billing=${billing}`);
  };

  return (
    <div className="flex flex-col gap-8">
      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => handleBillingChange("monthly")}
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
            billing === "monthly"
              ? "bg-[#4CAF50] text-white shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Monthly
        </button>
        <button
          type="button"
          onClick={() => handleBillingChange("yearly")}
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all inline-flex items-center gap-2 ${
            billing === "yearly"
              ? "bg-[#4CAF50] text-white shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Yearly
          <span
            className={`text-[10px] rounded-full px-1.5 py-0.5 ${
              billing === "yearly"
                ? "bg-white/20 text-white"
                : "bg-[#4CAF50]/15 text-[#4CAF50]"
            }`}
          >
            {yearlySavingLabel}
          </span>
        </button>
      </div>
      {mode === "display" && <PricesFor className="-mt-5 justify-center" />}

      {/* Plan grid */}
      <div
        className={`grid gap-6 text-left ${
          mode === "select" ? "sm:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-4"
        }`}
      >
        {plans.map((plan, i) => {
          const isHighlight = plan.highlight;
          const isSelected = mode === "select" && selectedPlanId === plan.id;
          const isSelectable = mode === "select" && !plan.isEnterprise;

          const cardClasses = isSelected
            ? "border-[#4CAF50] bg-white shadow-[0_8px_40px_-12px_rgba(76,175,80,0.45)] ring-2 ring-[#4CAF50]/40"
            : plan.isEnterprise
              ? "border-[#0A2540]/15 bg-gradient-to-b from-[#0A2540] to-[#0E2C44] text-white"
              : isHighlight && mode === "display"
                ? "border-[#4CAF50] bg-white shadow-[0_8px_40px_-12px_rgba(76,175,80,0.45)] lg:-translate-y-3"
                : "border-gray-200 bg-white";

          const Card = isSelectable
            ? motion.button
            : (motion.div as React.ComponentType<
                Parameters<typeof motion.div>[0] & { type?: string }
              >);

          return (
            <Card
              key={plan.id}
              type={isSelectable ? "button" : undefined}
              onClick={isSelectable ? () => onPlanSelect?.(plan.id) : undefined}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              className={`relative flex flex-col rounded-2xl border p-6 text-left transition-all ${
                isSelectable
                  ? "cursor-pointer hover:border-[#4CAF50]/60 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4CAF50] focus-visible:ring-offset-2"
                  : ""
              } ${cardClasses}`}
            >
              {/* Selected check (select mode) */}
              {isSelected && (
                <div className="absolute -top-3 -right-3 rounded-full bg-[#4CAF50] text-white p-1.5 shadow-md">
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                </div>
              )}

              {/* Most popular badge (display mode only — distracting in select) */}
              {isHighlight && mode === "display" && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#4CAF50] px-3 py-1 text-[11px] font-semibold text-white inline-flex items-center gap-1 shadow-sm">
                  <Sparkles className="h-3 w-3" />
                  Most popular
                </div>
              )}

              {/* Plan name */}
              <div className="flex items-center gap-2">
                {plan.isEnterprise && (
                  <Building2 className="h-4 w-4 text-[#7BAEC8]" />
                )}
                <h3
                  className={`text-lg font-bold ${
                    plan.isEnterprise ? "text-white" : "text-gray-900"
                  }`}
                >
                  {plan.name}
                </h3>
              </div>
              <p
                className={`mt-1 text-sm ${
                  plan.isEnterprise ? "text-[#A0BBCB]" : "text-gray-500"
                }`}
              >
                {plan.blurb}
              </p>

              {/* Price */}
              <div className="mt-5 mb-5">
                {plan.isEnterprise ? (
                  <div>
                    <div className="text-2xl font-bold text-white">
                      Custom pricing
                    </div>
                    <div className="text-sm text-[#A0BBCB] mt-1">
                      Tailored to your group
                    </div>
                  </div>
                ) : (
                  <PlanPrice plan={plan} cycle={billing} size="lg" />
                )}
              </div>

              {/* CTA — only in display mode (select mode card IS the CTA) */}
              {mode === "display" && plan.isEnterprise && (
                <Link to="/enterprise" className="w-full">
                  <Button className="w-full bg-[#4CAF50] text-white hover:bg-[#43a047]">
                    Talk to sales
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              )}
              {mode === "display" && !plan.isEnterprise && (
                <Button
                  onClick={() => handleDisplayClick(plan)}
                  className={`w-full ${
                    isHighlight
                      ? "bg-gradient-to-b from-[#51BB55] to-[#45A049] text-white hover:opacity-95"
                      : "bg-gray-900 text-white hover:bg-gray-800"
                  }`}
                >
                  Start free trial
                </Button>
              )}
              {mode === "select" && plan.isEnterprise && (
                <Link to="/enterprise" className="w-full">
                  <Button
                    type="button"
                    className="w-full bg-[#4CAF50] text-white hover:bg-[#43a047]"
                  >
                    Talk to sales
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              )}

              {/* Features */}
              <ul className="mt-6 flex flex-col gap-3">
                {plan.features.map((f, idx) => {
                  const isSectionLabel = f.endsWith("plus:");
                  if (isSectionLabel) {
                    return (
                      <li
                        key={idx}
                        className={`text-xs font-semibold uppercase tracking-wide pt-1 ${
                          plan.isEnterprise ? "text-[#7BAEC8]" : "text-gray-400"
                        }`}
                      >
                        {f}
                      </li>
                    );
                  }
                  return (
                    <li key={idx} className="flex items-start gap-2.5">
                      <Check className="h-4 w-4 mt-0.5 shrink-0 text-[#4CAF50]" />
                      <span
                        className={`text-sm ${
                          plan.isEnterprise ? "text-[#D5E2EB]" : "text-gray-600"
                        }`}
                      >
                        {f}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Card>
          );
        })}
      </div>

      {/* Enterprise reassurance line — display mode only */}
      {mode === "display" && (
        <p className="text-center text-sm text-gray-500">
          All plans include real accounting, offline mode, and
          VAT-ready books. Running multiple businesses?{" "}
          <Link
            to="/enterprise"
            className="text-[#4CAF50] font-medium hover:underline"
          >
            Talk to our team about Enterprise
          </Link>
          .
        </p>
      )}
    </div>
  );
};

export default SubscriptionPlan;
